import React, {useContext, useEffect, useState} from "react";
import {Box, Container, Dialog, DialogContent, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {AuthContext} from "../../context/AuthContext";
import {LibrettoPlan} from "../../utils/utils";
import AppSumoMark from "../../components/AppSumoMark";
import V2LoginForm from "../V2InitialLandingPage/V2LoginForm";
import V2SignupForm from "../V2InitialLandingPage/V2SignupForm";
import V2SignupMessage from "../V2InitialLandingPage/V2SignupMessage";

const steps = [
    ["Create your account", "Use Google or email. If you already have a Libretto account, sign in instead."],
    ["Add your AppSumo codes", "Open Settings → AppSumo and enter one to four codes."],
    ["Start using your lifetime plan", "Each code adds a tier. Your plan appears in Settings after redemption."],
];

const AppSumoRedeem = () => {
    const auth = useContext(AuthContext);
    const navigate = useNavigate();
    const [mode, setMode] = useState("signup");

    useEffect(() => {
        document.title = "Redeem your AppSumo codes | Libretto";
    }, []);

    useEffect(() => {
        if (auth.authReady && auth.isUserAuthenticated()) {
            navigate("/dashboard?settings=appsumo", {replace: true});
        }
    }, [auth, navigate]);

    const signupWithGoogle = () => auth.signupWithGoogle({planOverride: LibrettoPlan.StudioFree});
    const signupWithEmail = (options) => auth.signUpWithEmailAndPassword({
        ...options,
        planOverride: LibrettoPlan.StudioFree,
    });

    return (
        <Box sx={{minHeight: "100vh", bgcolor: "#F7F8FB", color: "#171A20"}}>
            <Container maxWidth="lg" sx={{py: {xs: 4, md: 8}}}>
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: {xs: 5, md: 9}}}>
                    <Box component="a" href="/" aria-label="Libretto home" sx={{display: "inline-flex"}}>
                        <Box component="img" src="/v2-logo.svg" alt="Libretto" sx={{width: 132, height: "auto"}} />
                    </Box>
                    <AppSumoMark sx={{width: 98, height: 32, fontSize: 12}} />
                </Box>

                <Box sx={{display: "grid", gridTemplateAreas: {xs: '"intro" "form" "steps"', md: '"intro form" "steps form"'}, gridTemplateColumns: {xs: "1fr", md: "minmax(0, 1fr) 430px"}, columnGap: {md: 10}, rowGap: {xs: 4, md: 5}, alignItems: "start", maxWidth: 1050, mx: "auto"}}>
                    <Box sx={{gridArea: "intro", pt: {md: 4}}}>
                        <Typography sx={{color: "#9C7200", fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", mb: 2}}>
                            Libretto × AppSumo
                        </Typography>
                        <Typography component="h1" sx={{fontFamily: '"Hedvig Letters Serif", serif', fontSize: {xs: 39, sm: 50, md: 54}, lineHeight: 1.08, letterSpacing: "-0.045em", fontWeight: 600, maxWidth: 560, mb: 2.5}}>
                            Your lifetime plan starts here.
                        </Typography>
                        <Typography sx={{fontSize: {xs: 16, sm: 18}, lineHeight: 1.6, color: "#626A78", maxWidth: 520}}>
                            Create a Libretto account, then redeem your AppSumo codes in Settings. No credit card is needed to redeem.
                        </Typography>
                    </Box>

                    <Box sx={{gridArea: "form", bgcolor: "#FFFFFF", border: "1px solid #E8EAF0", borderRadius: "20px", boxShadow: "0 14px 45px rgba(26, 32, 44, 0.06)", p: {xs: 3, sm: 4}}}>
                        {mode === "signup" ? (
                            <V2SignupForm
                                hideLogo
                                selectedPricingOption="Free"
                                signupWithGoogle={signupWithGoogle}
                                signUpWithEmailAndPassword={signupWithEmail}
                                closeSignupOpenLogin={() => setMode("login")}
                            />
                        ) : (
                            <V2LoginForm
                                hideLogo
                                signinWithGoogle={auth.signinWithGoogle}
                                signinWithEmail={auth.signinWithEmail}
                                handleLoginFormClose={() => {}}
                                handleSignUpFormOpen={() => setMode("signup")}
                                sendPasswordResetEmailForUser={auth.sendPasswordResetEmailForUser}
                            />
                        )}
                        <Typography sx={{borderTop: "1px solid #ECEEF2", pt: 2.5, mt: 3, textAlign: "center", color: "#747B87", fontSize: 12, lineHeight: 1.5}}>
                            Redeem your codes after you sign in. You can add up to four codes for Tier 4.
                        </Typography>
                    </Box>

                    <Box sx={{gridArea: "steps"}}>
                        <Box sx={{display: "flex", flexDirection: "column", gap: 3}}>
                            {steps.map(([title, description], index) => (
                                <Box key={title} sx={{display: "flex", gap: 2, alignItems: "flex-start"}}>
                                    <Box sx={{width: 32, height: 32, borderRadius: "50%", bgcolor: "#FFBD00", color: "#171717", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 13, fontWeight: 800}}>
                                        {index + 1}
                                    </Box>
                                    <Box>
                                        <Typography sx={{fontWeight: 700, fontSize: 15, mb: 0.3}}>{title}</Typography>
                                        <Typography sx={{fontSize: 14, lineHeight: 1.55, color: "#626A78"}}>{description}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        <Typography sx={{fontSize: 13, lineHeight: 1.5, color: "#808693", mt: 5, maxWidth: 500}}>
                            Signing up with email? Verify your email first, then return here to sign in and redeem your codes.
                        </Typography>
                    </Box>
                </Box>
            </Container>

            <Dialog open={auth.signUpStateMessageOpen} onClose={auth.handleSignupStateMessageClose} maxWidth="xs" fullWidth>
                <DialogContent><V2SignupMessage signUpStateMessage={auth.signUpStateMessage} /></DialogContent>
            </Dialog>
        </Box>
    );
};

export default AppSumoRedeem;
